import { canAccessModule, hasAnyRole } from '../utils/permissionUtils';
import { formatDate } from '../utils/dateFormat';

/**
 * Alerts: one-line warnings about something that needs doing, shown at the top of the screens where it can be acted on. WHICH alerts a
 * person gets, on which module, and with which figures, is decided by the server (AlertService, `GET /api/alerts?module=`), by role; this
 * file is how each one is worded and where its button goes. A screen only says where it is (<ModuleAlerts module="sales" section={tab} />).
 * To add an alert: the server computes it and says who sees it (AlertService + its test), then add an entry here:
 *
 *   key            the same key the server sends
 *   tone, icon     how it looks
 *   skipSections   { module: [sections] } where the screen already shows more than an alert would
 *   summarize(a, fmt)   { title, detail } for the line, from the server's figures (fmt formats an amount in the organization's currency)
 *   action(user, module, section)   { label, to } for the button, or null for none (it can depend on what the person can open)
 */

const list = (names) => (names || []).join(', ');
const plural = (n, one, many) => (n === 1 ? one : many);

const ALERTS = [
  {
    key: 'low-stock',
    tone: 'warning',
    icon: 'bi-exclamation-triangle',
    // Purchase Orders has the full list to order from
    skipSections: { purchase: ['purchase-orders'] },
    summarize: (a) => ({
      title: `${a.count} material${plural(a.count, '', 's')} running low:`,
      detail: a.count > (a.names || []).length ? `${list(a.names)} and ${a.count - a.names.length} more` : list(a.names)
    }),
    // Those who can open Purchase can order; storekeepers, who can't, are shown the stock instead (and nothing on the stock screen itself)
    action: (user, module, section) => {
      if (canAccessModule(user, 'purchase')) return { label: 'Order them', to: '/purchase/purchase-orders' };
      if (canAccessModule(user, 'inventory') && !(module === 'inventory' && section === 'stock-levels')) {
        return { label: 'See stock levels', to: '/inventory/stock-levels' };
      }
      return null;
    }
  },
  {
    key: 'overdue-customers',
    tone: 'warning',
    icon: 'bi-clock-history',
    skipSections: { accounts: ['receivables'] },
    // Salespeople are told that customers are overdue; the amount comes only to those the server meant it for
    summarize: (a, fmt) => ({
      title: `${a.count} customer${plural(a.count, ' has', 's have')} overdue bills`,
      detail: a.amount != null ? `${fmt(a.amount)} overdue` : ''
    }),
    action: (user) => {
      if (canAccessModule(user, 'accounts')) return { label: 'Open Receivables', to: '/accounts/receivables' };
      if (canAccessModule(user, 'sales')) return { label: 'See customers', to: '/sales/customers' };
      return null;
    }
  },
  {
    key: 'vendor-bills-due',
    tone: 'warning',
    icon: 'bi-calendar-event',
    skipSections: { accounts: ['payables'] },
    summarize: (a, fmt) => {
      const parts = [];
      if (Number(a.overdue) > 0) parts.push(`${fmt(a.overdue)} overdue`);
      if (Number(a.dueSoon) > 0) parts.push(`${fmt(a.dueSoon)} due within ${a.days || 7} days`);
      return { title: 'Vendor bills to pay:', detail: parts.join(', ') };
    },
    action: (user) => (canAccessModule(user, 'accounts') ? { label: 'Open Payables', to: '/accounts/payables' } : null)
  },
  {
    key: 'online-orders-new',
    tone: 'info',
    icon: 'bi-bag-heart',
    summarize: (a) => ({
      title: `${a.count} new online order${plural(a.count, '', 's')} to confirm`,
      detail: 'Buyers are waiting to hear from you.'
    }),
    action: (user) => (canAccessModule(user, 'store') ? { label: 'Open Online Store', to: '/store/orders' } : null)
  },
  {
    key: 'quotations-expiring',
    tone: 'info',
    icon: 'bi-hourglass-split',
    skipSections: { sales: ['quotations'] },
    summarize: (a) => ({
      title: `${a.count} open quotation${plural(a.count, ' expires', 's expire')} within ${a.days || 3} days:`,
      detail: list(a.names)
    }),
    action: (user) => (canAccessModule(user, 'sales') ? { label: 'See quotations', to: '/sales/quotations' } : null)
  },
  {
    key: 'over-credit-limit',
    tone: 'warning',
    icon: 'bi-shield-exclamation',
    summarize: (a) => ({
      title: `${a.count} customer${plural(a.count, ' is', 's are')} over their credit limit:`,
      detail: a.count > (a.names || []).length ? `${list(a.names)} and ${a.count - a.names.length} more` : list(a.names)
    }),
    action: (user) => {
      if (canAccessModule(user, 'accounts')) return { label: 'Open Receivables', to: '/accounts/receivables' };
      if (canAccessModule(user, 'sales')) return { label: 'See customers', to: '/sales/customers' };
      return null;
    }
  },
  {
    key: 'bank-lines-to-match',
    tone: 'warning',
    icon: 'bi-check2-square',
    // Bank Reconciliation is the list itself
    skipSections: { accounts: ['bank-reconciliation'] },
    summarize: (a) => ({
      title: `${a.count} bank statement line${plural(a.count, '', 's')} still to match`,
      detail: list(a.names)
    }),
    action: (user) => (canAccessModule(user, 'accounts') ? { label: 'Open Bank Reconciliation', to: '/accounts/bank-reconciliation' } : null)
  },
  {
    key: 'lots-awaiting-qc',
    tone: 'warning',
    icon: 'bi-clipboard-check',
    summarize: (a) => ({
      title: `${a.count} received lot${plural(a.count, '', 's')} waiting for a quality check`,
      detail: 'The stock cannot be issued until it passes.'
    }),
    action: (user) => (canAccessModule(user, 'quality') ? { label: 'Open Quality', to: '/quality' } : null)
  },
  {
    key: 'lots-expiring',
    tone: 'warning',
    icon: 'bi-hourglass-split',
    // Stock Ageing is the list itself
    skipSections: { inventory: ['stock-ageing'] },
    summarize: (a) => {
      const parts = [];
      if (a.expired > 0) parts.push(`${a.expired} lot${plural(a.expired, '', 's')} past ${plural(a.expired, 'its', 'their')} expiry date`);
      if (a.soon > 0) parts.push(`${a.soon} expiring within ${a.days || 30} days`);
      return { title: 'Stock going bad:', detail: parts.join(', ') };
    },
    // Stock Ageing (managers) lists the lots; the others are shown the stock
    action: (user) => {
      if (!canAccessModule(user, 'inventory')) return null;
      return hasAnyRole(user, ['ORG_ADMIN', 'INV_MGR', 'PROD_MGR'])
        ? { label: 'See Stock Ageing', to: '/inventory/stock-ageing' }
        : { label: 'See stock levels', to: '/inventory/stock-levels' };
    }
  },
  {
    key: 'tds-to-deposit',
    tone: 'warning',
    icon: 'bi-bank',
    // The TDS screen is the list itself
    skipSections: { accounts: ['tds'] },
    summarize: (a, fmt) => {
      const parts = [];
      if (Number(a.overdue) > 0) parts.push(`${fmt(a.overdue)} past its due date`);
      if (Number(a.dueSoon) > 0) parts.push(`${fmt(a.dueSoon)} due within ${a.days || 7} days`);
      return { title: 'TDS to deposit:', detail: parts.join(', ') };
    },
    action: (user) => (canAccessModule(user, 'accounts') ? { label: 'Open TDS', to: '/accounts/tds' } : null)
  },
  {
    key: 'plan-ending',
    tone: 'warning',
    icon: 'bi-calendar-x',
    summarize: (a) => ({
      title: `${a.trial ? 'Your trial' : 'Your plan'} ${a.days === 0 ? 'ends today' : `ends in ${a.days} day${plural(a.days, '', 's')}`}`,
      detail: a.endDate ? `on ${formatDate(a.endDate)}. Renew or upgrade to keep working without a break.` : ''
    }),
    action: (user) => (canAccessModule(user, 'org-admin') ? { label: 'See license', to: '/org-admin/license' } : null)
  }
];

const BY_KEY = Object.fromEntries(ALERTS.map((a) => [a.key, a]));

/** How to word and link an alert the server sent; null for a key this version does not know (it is simply not shown). */
export function describeAlert(serverAlert, user, module, section, fmt) {
  const def = BY_KEY[serverAlert.key];
  if (!def) return null;
  if ((def.skipSections?.[module] || []).includes(section)) return null;
  const { title, detail } = def.summarize(serverAlert, fmt);
  return { key: def.key, tone: def.tone, icon: def.icon, title, detail, action: def.action(user, module, section) };
}

export { ALERTS };
